
import {
  Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, ListItemText, MenuItem, Select, Stack, TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  assignUserRole, assignUserShops, resetUserPassword,
  unassignUserRole, unassignUserShops,
} from "../../../services/usersService";
import { getApiErrorMessage } from "../../../utils/apiData";

function SquareDialog({ open, onClose, title, children, actions }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
      <DialogTitle sx={{ fontSize: "0.9rem", fontWeight: 850, borderBottom: "1px solid #E3E7EC" }}>{title}</DialogTitle>
      <DialogContent sx={{ p: 2, bgcolor: "#FBFCFD" }}>{children}</DialogContent>
      <DialogActions sx={{ borderTop: "1px solid #E3E7EC" }}>{actions}</DialogActions>
    </Dialog>
  );
}

export function UserAdminActions({ open, onClose, user, roles = [], shops = [] }) {
  const client = useQueryClient();
  const [action, setAction] = useState(null);
  const [message, setMessage] = useState(null);
  const [roleId, setRoleId] = useState(user?.role_id || user?.role?.id || "");
  const [shopIds, setShopIds] = useState((user?.shops || []).map((s) => s.id));
  const [password, setPassword] = useState({ password: "", password_confirmation: "" });

  const done = (text) => {
    client.invalidateQueries({ queryKey: ["users"] });
    setAction(null);
    setMessage(text);
  };
  const fail = (error) => setMessage(getApiErrorMessage(error));

  const reset = useMutation({
    mutationFn: () => resetUserPassword(user.id, password),
    onSuccess: (r) => done(r?.message || "Password reset successfully."),
    onError: fail,
  });
  const assignRole = useMutation({
    mutationFn: () => assignUserRole(user.id, roleId),
    onSuccess: (r) => done(r?.message || "Role assigned successfully."),
    onError: fail,
  });
  const unassignRole = useMutation({
    mutationFn: () => unassignUserRole(user.id),
    onSuccess: (r) => done(r?.message || "Role removed successfully."),
    onError: fail,
  });
  const assignShops = useMutation({
    mutationFn: () => assignUserShops(user.id, shopIds),
    onSuccess: (r) => done(r?.message || "Shops assigned successfully."),
    onError: fail,
  });
  const unassignShops = useMutation({
    mutationFn: () => unassignUserShops(user.id, shopIds),
    onSuccess: (r) => done(r?.message || "Shops removed successfully."),
    onError: fail,
  });

  return (
    <>
      <SquareDialog
        open={open}
        onClose={onClose}
        title={`Manage ${user?.name || "user"}`}
        actions={<Button onClick={onClose} color="inherit" sx={{ borderRadius: 0 }}>Close</Button>}
      >
        <Stack spacing={1}>
          <Button variant="outlined" color="inherit" onClick={() => setAction("reset")} sx={{ borderRadius: 0 }}>Reset password</Button>
          <Button variant="outlined" color="inherit" onClick={() => setAction("assign-role")} sx={{ borderRadius: 0 }}>Assign role</Button>
          <Button variant="outlined" color="inherit" onClick={() => setAction("unassign-role")} sx={{ borderRadius: 0 }}>Unassign role</Button>
          <Button variant="outlined" color="inherit" onClick={() => setAction("assign-shops")} sx={{ borderRadius: 0 }}>Assign shops</Button>
          <Button variant="outlined" color="error" onClick={() => setAction("unassign-shops")} sx={{ borderRadius: 0 }}>Unassign shops</Button>
        </Stack>
      </SquareDialog>

      <SquareDialog
        open={action === "reset"}
        onClose={() => setAction(null)}
        title="Reset user password"
        actions={
          <>
            <Button onClick={() => setAction(null)} color="inherit" sx={{ borderRadius: 0 }}>Cancel</Button>
            <Button onClick={() => reset.mutate()} variant="contained" disabled={reset.isPending} sx={{ borderRadius: 0 }}>
              {reset.isPending ? "Resetting..." : "Reset password"}
            </Button>
          </>
        }
      >
        <Stack spacing={1}>
          <TextField label="New password" type="password" value={password.password} onChange={(e) => setPassword((p) => ({ ...p, password: e.target.value }))} sx={{ "& .MuiInputBase-root": { borderRadius: 0 } }} />
          <TextField label="Confirm password" type="password" value={password.password_confirmation} onChange={(e) => setPassword((p) => ({ ...p, password_confirmation: e.target.value }))} sx={{ "& .MuiInputBase-root": { borderRadius: 0 } }} />
        </Stack>
      </SquareDialog>

      <SquareDialog
        open={action === "assign-role"}
        onClose={() => setAction(null)}
        title="Assign role"
        actions={
          <>
            <Button onClick={() => setAction(null)} color="inherit" sx={{ borderRadius: 0 }}>Cancel</Button>
            <Button onClick={() => assignRole.mutate()} variant="contained" disabled={assignRole.isPending || !roleId} sx={{ borderRadius: 0 }}>Assign role</Button>
          </>
        }
      >
        <FormControl fullWidth>
          <InputLabel id="role-label">Role</InputLabel>
          <Select labelId="role-label" label="Role" value={roleId} onChange={(e) => setRoleId(e.target.value)} sx={{ borderRadius: 0 }}>
            {roles.map((role) => <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>)}
          </Select>
        </FormControl>
      </SquareDialog>

      <SquareDialog
        open={action === "unassign-role"}
        onClose={() => setAction(null)}
        title="Unassign role"
        actions={
          <>
            <Button onClick={() => setAction(null)} color="inherit" sx={{ borderRadius: 0 }}>Cancel</Button>
            <Button onClick={() => unassignRole.mutate()} color="error" variant="contained" disabled={unassignRole.isPending} sx={{ borderRadius: 0 }}>Unassign role</Button>
          </>
        }
      >
        <Typography sx={{ fontSize: "0.7rem" }}>Remove the current role from <strong>{user?.name}</strong>?</Typography>
      </SquareDialog>

      <SquareDialog
        open={action === "assign-shops" || action === "unassign-shops"}
        onClose={() => setAction(null)}
        title={action === "assign-shops" ? "Assign shops" : "Unassign shops"}
        actions={
          <>
            <Button onClick={() => setAction(null)} color="inherit" sx={{ borderRadius: 0 }}>Cancel</Button>
            <Button
              onClick={() => action === "assign-shops" ? assignShops.mutate() : unassignShops.mutate()}
              color={action === "unassign-shops" ? "error" : "primary"}
              variant="contained"
              sx={{ borderRadius: 0 }}
            >
              {action === "assign-shops" ? "Assign shops" : "Unassign shops"}
            </Button>
          </>
        }
      >
        <FormControl fullWidth>
          <InputLabel id="shops-label">Shops</InputLabel>
          <Select
            labelId="shops-label"
            label="Shops"
            multiple
            value={shopIds}
            onChange={(e) => setShopIds(e.target.value)}
            renderValue={(selected) => `${selected.length} shop${selected.length === 1 ? "" : "s"} selected`}
            sx={{ borderRadius: 0 }}
          >
            {shops.map((shop) => (
              <MenuItem key={shop.id} value={shop.id}>
                <Checkbox checked={shopIds.includes(shop.id)} />
                <ListItemText primary={shop.shop_name} secondary={shop.shop_code} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </SquareDialog>

      <Dialog open={Boolean(message)} onClose={() => setMessage(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle sx={{ fontSize: "0.82rem", fontWeight: 850 }}>User management</DialogTitle>
        <DialogContent dividers><Typography sx={{ fontSize: "0.7rem" }}>{message}</Typography></DialogContent>
        <DialogActions><Button onClick={() => setMessage(null)} variant="contained" sx={{ borderRadius: 0 }}>OK</Button></DialogActions>
      </Dialog>
    </>
  );
}
